<script setup lang="ts">
import * as R from 'ramda'
import { computed } from 'vue'

import {
  Participant,
  RugbyLeagueIncidentId,
  RugbyLeagueTackleMadeAttribute,
} from '@collector/sportsapi-client-legacy'
import { useIncidents } from '@desktop/views/Relation/composables'

type Props = {
  participant: Participant
}

const props = defineProps<Props>()

const tacklesMadeMap = new Map<RugbyLeagueTackleMadeAttribute, number>([
  [RugbyLeagueTackleMadeAttribute.One, 1],
  [RugbyLeagueTackleMadeAttribute.Two, 2],
  [RugbyLeagueTackleMadeAttribute.Three, 3],
  [RugbyLeagueTackleMadeAttribute.Four, 4],
  [RugbyLeagueTackleMadeAttribute.Five, 5],
  [RugbyLeagueTackleMadeAttribute.Six, 6],
])

const incidentsResettingTackleCounter = new Set<RugbyLeagueIncidentId>([
  RugbyLeagueIncidentId.FirstHalf,
  RugbyLeagueIncidentId.Halftime,
  RugbyLeagueIncidentId.SecondHalf,
  RugbyLeagueIncidentId.WaitingForExtratime,
  RugbyLeagueIncidentId.ExtratimeFirstHalf,
  RugbyLeagueIncidentId.ExtratimeHalftime,
  RugbyLeagueIncidentId.ExtratimeSecondHalf,
  RugbyLeagueIncidentId.WaitingForPenalty,
  RugbyLeagueIncidentId.PenaltiesStarted,
  RugbyLeagueIncidentId.FinishedRegularTime,
  RugbyLeagueIncidentId.FinishedAfterExtratime,
  RugbyLeagueIncidentId.FinishedAfterPenalties,
])

// composables
const { incidents } = useIncidents()

const reversedIncidents = computed(() => R.reverse(incidents.value))

const lastTackleIdx = computed(() =>
  R.findIndex(
    (incident) => incident.incident_id === RugbyLeagueIncidentId.TackleMade,
    reversedIncidents.value,
  ),
)

const lastIncidentResettingTackleCounterIdx = computed(() => {
  if (lastTackleIdx.value === -1) return -1

  for (let i = 0; i < lastTackleIdx.value; i++) {
    if (
      incidentsResettingTackleCounter.has(
        reversedIncidents.value[i].incident_id as RugbyLeagueIncidentId,
      )
    )
      return i
  }

  return -1
})

const shouldResetTackleCounter = computed(
  () =>
    lastIncidentResettingTackleCounterIdx.value >= 0 &&
    lastIncidentResettingTackleCounterIdx.value < lastTackleIdx.value,
)

const isLastTackleMadeByCurrentParticipant = computed(
  () =>
    reversedIncidents.value[lastTackleIdx.value]?.participant_team_id ===
    props.participant.id,
)

const tacklesMadeCounter = computed(() => {
  if (!isLastTackleMadeByCurrentParticipant.value) return 0

  if (shouldResetTackleCounter.value) return 0

  const tacklesMadeAttribute = reversedIncidents.value[lastTackleIdx.value]
    .attribute_ids?.[0] as RugbyLeagueTackleMadeAttribute | undefined

  if (!tacklesMadeAttribute) return 0

  return tacklesMadeMap.get(tacklesMadeAttribute) || 0
})
</script>

<template>
  <div
    class="flex h-7 items-baseline justify-center space-x-1"
    data-testid="tackles-counter"
  >
    <span class="text-sm font-semibold">TACKLES</span>
    <span class="text-base font-bold">{{ tacklesMadeCounter }}</span>
  </div>
</template>
